import React, { useState } from "react";
import { motion } from "framer-motion";
import { List } from "@refinedev/antd";
import { useList, useCreate, useUpdate, useDelete } from "@refinedev/core";
import { useTranslate } from "@refinedev/core";
import { Button, Space, Row, Col, Card as AntCard, message, Modal } from "antd";
import { PlusOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { AssignmentRuleCard } from "./components/AssignmentRuleCard";

interface AssignmentRule {
  id: string;
  name: string;
  description?: string;
  assignmentType: "user" | "service_provider" | "group";
  targetUserId?: number;
  targetServiceProviderId?: number;
  targetGroupId?: number;
  strategy: "immediate" | "delayed";
  delayMinutes?: number;
  productIds?: string[] | number[]; // May come from API as array of IDs
  products?: Array<{ id: number | string; [key: string]: any }>; // API returns full product objects
  accountCondition: "all_accounts" | "specific_accounts";
  accountIds?: string[] | number[]; // May come from API as array of IDs
  accounts?: Array<{ id: number | string; [key: string]: any }>; // API returns full account objects
  priority: number;
  isActive: boolean;
  _isNew?: boolean; // Internal flag for unsaved rules
}

interface AssignmentRuleCardProps {
  rule: AssignmentRule;
  index: number;
  totalRules: number;
  onUpdate: (id: string, values: Partial<AssignmentRule>) => void;
  onDelete: (id: string) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onLocalUpdate?: (id: string, values: Partial<AssignmentRule>) => void; // For updating local state
  usersData?: any;
  groupsData?: any;
  serviceProvidersData?: any;
}

export const AssignmentRulesList: React.FC<{ hideHeader?: boolean }> = ({ hideHeader }) => {
  const _t = useTranslate();
  const [localRules, setLocalRules] = useState<AssignmentRule[]>([]);
  const [nextTempId, setNextTempId] = useState(1);
  const [optimisticUpdates, setOptimisticUpdates] = useState<
    Record<string, Partial<AssignmentRule>>
  >({});

  const { data, isLoading, refetch } = useList<AssignmentRule>({
    resource: "assignment-rules",
    pagination: {
      mode: "off",
    },
    sorters: [
      {
        field: "priority",
        order: "desc",
      },
    ],
    meta: {
      query: {
        page: 1,
        limit: 1000,
      },
    },
  });

  const { data: usersData } = useList({
    resource: "users",
    pagination: { mode: "off" },
  });
  const { data: groupsData } = useList({
    resource: "user-groups",
    pagination: { mode: "off" },
  });
  const { data: serviceProvidersData } = useList({
    resource: "service-providers",
    pagination: { mode: "off" },
  });

  const { mutate: createRule } = useCreate();
  const { mutate: updateRule } = useUpdate();
  const { mutate: deleteRule } = useDelete();

  // Handle API response structure: { data: [...], total, page, limit }
  const savedRules = React.useMemo(() => {
    if (!data?.data) return [];
    const responseData = data.data as any;
    // If data.data is an array, use it directly
    if (Array.isArray(responseData)) {
      return responseData;
    }
    // If data.data is an object with a data property, extract it
    if (
      responseData &&
      typeof responseData === "object" &&
      "data" in responseData &&
      Array.isArray(responseData.data)
    ) {
      return responseData.data;
    }
    return [];
  }, [data]);

  // Combine saved rules and local rules, apply optimistic updates, sorted by priority (highest first)
  const allRules = React.useMemo(() => {
    const combined = [...savedRules, ...localRules];
    const merged = combined.map((r) => {
      const patch = optimisticUpdates[String(r.id)];
      return patch ? { ...r, ...patch } : r;
    });
    return merged.sort((a, b) => (b.priority || 0) - (a.priority || 0));
  }, [savedRules, localRules, optimisticUpdates]);

  const handleCreateNew = () => {
    const maxPriority =
      allRules.length > 0
        ? Math.max(...allRules.map((r) => r.priority || 0))
        : -1;

    const newRule: AssignmentRule = {
      id: `temp-${nextTempId}`,
      name: _t("assignment-rules.fields.newRule", "New Rule"),
      description: "",
      assignmentType: "user",
      strategy: "immediate",
      productIds: [],
      accountCondition: "all_accounts",
      priority: maxPriority + 1, // Highest priority (appears at top)
      isActive: true,
      _isNew: true,
    };

    setLocalRules((prev) => [newRule, ...prev]);
    setNextTempId((prev) => prev + 1);
  };

  const handleUpdate = (
    id: string | number,
    values: Partial<AssignmentRule>,
  ) => {
    const idString = String(id);
    const isNewRule = idString.startsWith("temp-");

    // Clean up values - remove internal fields and API response fields
    const {
      _isNew,
      products,
      accounts,
      tenant,
      tenantId,
      createdAt,
      updatedAt,
      deletedAt,
      __entity,
      ...cleanValues
    } = values as any;

    if (isNewRule) {
      // Create new rule
      createRule(
        {
          resource: "assignment-rules",
          values: cleanValues,
        },
        {
          onSuccess: () => {
            message.success(
              _t(
                "assignment-rules.messages.createSuccess",
                "Rule created successfully",
              ),
            );
            // Remove from local rules
            setLocalRules((prev) => prev.filter((r) => r.id !== id));
            refetch();
          },
          onError: (error: any) => {
            console.error("Create error:", error);
            message.error(
              _t(
                "assignment-rules.messages.createError",
                "Failed to create rule",
              ),
            );
          },
        },
      );
    } else {
      // Optimistic update so assignee (group/provider) shows immediately
      setOptimisticUpdates((prev) => ({
        ...prev,
        [idString]: { ...prev[idString], ...cleanValues },
      }));
      // Update existing rule
      updateRule(
        {
          resource: "assignment-rules",
          id: idString,
          values: cleanValues,
        },
        {
          onSuccess: () => {
            message.success(
              _t(
                "assignment-rules.messages.updateSuccess",
                "Rule updated successfully",
              ),
            );
            setOptimisticUpdates((prev) => {
              const next = { ...prev };
              delete next[idString];
              return next;
            });
            refetch();
          },
          onError: (error: any) => {
            console.error("Update error:", error);
            message.error(
              _t(
                "assignment-rules.messages.updateError",
                "Failed to update rule",
              ),
            );
            setOptimisticUpdates((prev) => {
              const next = { ...prev };
              delete next[idString];
              return next;
            });
          },
        },
      );
    }
  };

  const handleDelete = (id: string | number) => {
    const idString = String(id);
    const isNewRule = idString.startsWith("temp-");

    // Find the rule to get its name for the confirmation message
    const ruleToDelete = isNewRule
      ? localRules.find((r) => String(r.id) === idString)
      : allRules.find((r) => String(r.id) === idString);

    const ruleName =
      ruleToDelete?.name || _t("assignment-rules.fields.newRule", "New Rule");

    Modal.confirm({
      title: _t("assignment-rules.actions.delete", "Delete Rule"),
      icon: <ExclamationCircleOutlined />,
      content: _t(
        "assignment-rules.messages.deleteConfirm",
        `Are you sure you want to delete the rule "${ruleName}"? This action cannot be undone.`,
      ).replace("{{name}}", ruleName),
      okText: _t("buttons.delete", "Delete"),
      okType: "danger",
      cancelText: _t("buttons.cancel", "Cancel"),
      onOk: () => {
        if (isNewRule) {
          // Just remove from local rules
          setLocalRules((prev) =>
            prev.filter((r) => String(r.id) !== idString),
          );
          message.success(
            _t(
              "assignment-rules.messages.deleteSuccess",
              "Rule deleted successfully",
            ),
          );
        } else {
          // Delete from API
          deleteRule(
            {
              resource: "assignment-rules",
              id: idString,
            },
            {
              onSuccess: () => {
                message.success(
                  _t(
                    "assignment-rules.messages.deleteSuccess",
                    "Rule deleted successfully",
                  ),
                );
                refetch();
              },
              onError: () => {
                message.error(
                  _t(
                    "assignment-rules.messages.deleteError",
                    "Failed to delete rule",
                  ),
                );
              },
            },
          );
        }
      },
    });
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;

    const currentRule = allRules[index];
    const previousRule = allRules[index - 1];

    // Swap priorities
    const tempPriority = currentRule.priority;

    // Update local state for new rules
    if (currentRule._isNew) {
      setLocalRules((prev) =>
        prev.map((r) =>
          r.id === currentRule.id
            ? { ...r, priority: previousRule.priority }
            : r,
        ),
      );
    }
    if (previousRule._isNew) {
      setLocalRules((prev) =>
        prev.map((r) =>
          r.id === previousRule.id ? { ...r, priority: tempPriority } : r,
        ),
      );
    }

    // Update API for saved rules
    if (!currentRule._isNew) {
      updateRule(
        {
          resource: "assignment-rules",
          id: currentRule.id,
          values: { priority: previousRule.priority },
        },
        {
          onSuccess: () => {
            if (!previousRule._isNew) {
              updateRule(
                {
                  resource: "assignment-rules",
                  id: previousRule.id,
                  values: { priority: tempPriority },
                },
                {
                  onSuccess: () => {
                    refetch();
                  },
                },
              );
            } else {
              refetch();
            }
          },
        },
      );
    } else if (!previousRule._isNew) {
      updateRule(
        {
          resource: "assignment-rules",
          id: previousRule.id,
          values: { priority: tempPriority },
        },
        {
          onSuccess: () => {
            refetch();
          },
        },
      );
    }
  };

  const handleMoveDown = (index: number) => {
    if (index === allRules.length - 1) return;

    const currentRule = allRules[index];
    const nextRule = allRules[index + 1];

    // Swap priorities
    const tempPriority = currentRule.priority;

    // Update local state for new rules
    if (currentRule._isNew) {
      setLocalRules((prev) =>
        prev.map((r) =>
          r.id === currentRule.id ? { ...r, priority: nextRule.priority } : r,
        ),
      );
    }
    if (nextRule._isNew) {
      setLocalRules((prev) =>
        prev.map((r) =>
          r.id === nextRule.id ? { ...r, priority: tempPriority } : r,
        ),
      );
    }

    // Update API for saved rules
    if (!currentRule._isNew) {
      updateRule(
        {
          resource: "assignment-rules",
          id: currentRule.id,
          values: { priority: nextRule.priority },
        },
        {
          onSuccess: () => {
            if (!nextRule._isNew) {
              updateRule(
                {
                  resource: "assignment-rules",
                  id: nextRule.id,
                  values: { priority: tempPriority },
                },
                {
                  onSuccess: () => {
                    refetch();
                  },
                },
              );
            } else {
              refetch();
            }
          },
        },
      );
    } else if (!nextRule._isNew) {
      updateRule(
        {
          resource: "assignment-rules",
          id: nextRule.id,
          values: { priority: tempPriority },
        },
        {
          onSuccess: () => {
            refetch();
          },
        },
      );
    }
  };

  const handleLocalUpdate = (id: string, values: Partial<AssignmentRule>) => {
    setLocalRules((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...values } : r)),
    );
  };

  return (
    <div className="max-w-7xl mx-auto">
      <List
        title={hideHeader ? false : _t("assignment-rules.title")}
        breadcrumb={hideHeader ? false : undefined}
        headerProps={{
          extra: (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateNew}
            >
              {_t("assignment-rules.actions.create", "Create New Rule")}
            </Button>
          ),
        }}
      >
        <Row gutter={[16, 16]} justify="center" className="w-full">
          {isLoading ? (
            <Col xs={24} sm={24} md={20} lg={16} xl={12}>
              <AntCard loading />
            </Col>
          ) : allRules.length === 0 ? (
            <Col xs={24} sm={24} md={20} lg={16} xl={12}>
              <AntCard>
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  {_t(
                    "assignment-rules.noRules",
                    "No assignment rules found. Create your first rule.",
                  )}
                </div>
              </AntCard>
            </Col>
          ) : (
            allRules.map((rule, index) => (
              <Col key={rule.id} xs={24}>
                <motion.div
                  layout
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                >
                  <AssignmentRuleCard
                    rule={rule}
                    index={index}
                    totalRules={allRules.length}
                    onUpdate={handleUpdate}
                    onDelete={handleDelete}
                    onMoveUp={handleMoveUp}
                    onMoveDown={handleMoveDown}
                    onLocalUpdate={rule._isNew ? handleLocalUpdate : undefined}
                    usersData={usersData}
                    groupsData={groupsData}
                    serviceProvidersData={serviceProvidersData}
                  />
                </motion.div>
              </Col>
            ))
          )}
        </Row>
      </List>
    </div>
  );
};
